import {useEffect,useMemo,useState} from 'react';
import {StaffMembershipPayment} from './StaffMembershipPayment';
import {StaffGiftCardPayment} from './StaffGiftCardPayment';
import {StaffTicketCardPayment} from './StaffTicketCardPayment';
import {SaleTicketHelp} from './SaleTicketHelp';
import {omniCategoryNames,parseOmnibarCategoryCommand} from './saleOmnibarCategories';

export type LinkedAppointmentSale={
  appointmentId:string;customerId:string;customerName:string;
  serviceName:string;serviceCode?:string;saleId:string;payerOther:boolean;
  preselectedDiscount?:{id:string;code:string;title:string}|null;
  linkedAppointments?:Array<{appointmentId:string;customerId:string;
    customerName:string;serviceName:string;serviceCode:string;saleId:string}>;
};
type GiftDeliverySetup={
  reservationId:string;customerId:string|null;
  deliveryMode:'none'|'immediate'|'scheduled';
  recipientEmail:string|null;recipientName:string;senderName:string;
  personalMessage:string;scheduledAt:string|null;
};
export type TicketLine={
  key:string;serviceId:string;packageId?:string;legacyCashPackageId?:string;productId?:string;giftCardId?:string;
  reservedGift?:GiftDeliverySetup;membershipId?:string;recurringPriceCents?:number|null;
  repeatPeriod?:string|null;creditsPerUnit?:number;name:string;durationMinutes:number;
  priceCents:number|null;quantity:number;appointmentId:string|null;
};
export type SaleDraft={
  id:number;ticketKey:string;locationCode:string;customerId:string|null;customerName:string|null;
  linkedAppointment:LinkedAppointmentSale|null;
  linkedAppointments?:LinkedAppointmentSale[];
  lines:TicketLine[];autoItemInitialized:boolean;
  saleComment?:string;
  preselectedDiscount?:{id:string;code:string;title:string}|null;
  manualDiscount?:{id:string;code:string;title:string;description:string}|null;
};
type Customer={customerId:string;displayName:string;homeLocationCode:string|null};
type CustomerBody={ok:boolean;data?:{customers:Customer[];hasMore:boolean};
  error?:{message?:string}};
type OmniItem={id:string;name:string;code?:string;
  kind:'service'|'package'|'membership'|'giftCard'|'discount'|'product';priceCents:number|null;description?:string;
  durationMinutes?:number;creditsPerUnit?:number;serviceId?:string;legacyCashPackageId?:string;
  recurringPriceCents?:number|null;repeatPeriod?:string|null;
  newOnly?:boolean;mailingListOnly?:boolean;uses?:number;
  useLimit?:number|null;perCustomerLimit?:number|null;
  availableFrom?:string|null;availableUntil?:string|null;
  minPurchaseCents?:number|null;employeeInstructions?:string;};
type OmniCatalog={services:OmniItem[];packages:OmniItem[];memberships:OmniItem[];
  giftCards:OmniItem[];discounts:OmniItem[];products:OmniItem[]};
type ComplimentaryReceipt={transactionId:string;creditId:string;
  originalCents:number;discountCents:number;netCents:0;
  checkedOutAppointmentId:string|null;replayed:boolean};
type CashReceipt={transactionId:string;totalCents:number;receivedCents:number;
  changeCents:number;changeDenominations:Record<string,number>;
  checkedOutAppointmentIds:string[];replayed:boolean};
const dollar=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD'}).format(cents/100);
const billName=(cents:number)=>cents>=100?
  '$'+(cents/100).toFixed(0):cents+'¢';

/** Bottom-floating Helm sale ticket. OMNIBAR is a real touch/focus input.
 * Only source-verified services have the sale/credit implementation today.
 */
export function SalesTicketWorkspace({draft,onUpdateCustomer,onNewCustomer,
  onMinimize,onCancelDraft,onUpdateLines,onRestoreGiftLines,onReconcileGiftBuyer,onAutoItemInitialized,
  onRemovePreselectedDiscount,onUpdateManualDiscount,onUpdateSaleComment,onComplete,locationName,
  onHelpFeedback}:{
  draft:SaleDraft;locationName:string;onUpdateCustomer:(customer:Customer|null)=>void;
  onNewCustomer:()=>void;onMinimize:()=>void;
  onCancelDraft:()=>Promise<void>;
  onUpdateLines:(lines:TicketLine[])=>void;
  onRestoreGiftLines:(lines:TicketLine[])=>void;
  onReconcileGiftBuyer:(reservationIds:string[],buyerId:string)=>void;
  onAutoItemInitialized:(lines:TicketLine[])=>void;
  onRemovePreselectedDiscount:()=>Promise<void>;
  onUpdateManualDiscount:(selection:SaleDraft['manualDiscount'])=>void;
  onUpdateSaleComment:(comment:string)=>void;
  onComplete:()=>void;
  onHelpFeedback:()=>void;
}){
  const [query,setQuery]=useState('');
  const [helpMode,setHelpMode]=useState<'pos'|'omnibar'|null>(null);
  const [browseCategory,setBrowseCategory]=useState<keyof OmniCatalog|null>(null);
  const [busy,setBusy]=useState(false);
  const [candidates,setCandidates]=useState<Customer[]>([]);
  const [cancelling,setCancelling]=useState(false);
  const [cancelConfirm,setCancelConfirm]=useState(false);
  const [removingDiscount,setRemovingDiscount]=useState(false);
  const [receiptChecked,setReceiptChecked]=useState(true);
  const [customerReceiptPref,setCustomerReceiptPref]=useState(true);
  const [receiptEmail,setReceiptEmail]=useState('');
  const [receiptEmailEdit,setReceiptEmailEdit]=useState(false);
  const [error,setError]=useState('');
  const [commentSaving,setCommentSaving]=useState(false);
  const [commentError,setCommentError]=useState('');
  const [commentOpen,setCommentOpen]=useState(false);
  const [omniQuery,setOmniQuery]=useState('');
  const [omniActive,setOmniActive]=useState(false);
  const [catalog,setCatalog]=useState<OmniCatalog|null>(null);
  const [catalogBusy,setCatalogBusy]=useState(false);
  const [giftBusy,setGiftBusy]=useState(false);
  const [editingGiftKey,setEditingGiftKey]=useState<string|null>(null);
  const [giftEmailMode,setGiftEmailMode]=useState<'immediate'|'scheduled'>('immediate');
  const [giftRecipientEmail,setGiftRecipientEmail]=useState('');
  const [giftRecipientName,setGiftRecipientName]=useState('');
  const [giftSenderName,setGiftSenderName]=useState('');
  const [giftMessage,setGiftMessage]=useState('');
  const [giftSchedule,setGiftSchedule]=useState('');
  const [giftStatus,setGiftStatus]=useState('');
  const [cashOpen,setCashOpen]=useState(false);
  const [cashBusy,setCashBusy]=useState(false);
  const [cardBusy,setCardBusy]=useState(false);
  const [cardOrderId,setCardOrderId]=useState<string|null>(null);
  const [cardOrderIsRetail,setCardOrderIsRetail]=useState(false);
  const [cardOrderStatus,setCardOrderStatus]=useState('');
  const [cardCheckoutSnapshot,setCardCheckoutSnapshot]=useState<string|null>(null);
  const [tenderAmount,setTenderAmount]=useState('');
  const [receipt,setReceipt]=useState<CashReceipt|null>(null);
  const [complimentaryReceipt,setComplimentaryReceipt]=useState<ComplimentaryReceipt|null>(null);
  const [complimentaryBusy,setComplimentaryBusy]=useState(false);
  const [transactionId,setTransactionId]=useState(()=>crypto.randomUUID());
  const [register,setRegister]=useState<{expectedCents:number;synthetic:boolean;
    expectedDenominations:Record<string,number>}|null>(null);
  useEffect(()=>{
    setQuery('');setCandidates([]);setError('');
    setOmniQuery('');setOmniActive(false);setCashOpen(false);
    setEditingGiftKey(null);setGiftStatus('');setCommentOpen(false);
    setReceiptChecked(true);
    setReceiptEmail('');setReceiptEmailEdit(false);
    setTenderAmount('');setReceipt(null);setComplimentaryReceipt(null);
    setComplimentaryBusy(false);setTransactionId(crypto.randomUUID());
    setCardOrderId(null);setCardOrderIsRetail(false);setCardOrderStatus('');setCardBusy(false);
    setCardCheckoutSnapshot(null);
  },[draft.id]);
  useEffect(()=>{
    const controller=new AbortController();
    const query=new URLSearchParams({
      location:draft.locationCode,ticketKey:draft.ticketKey,
    });
    void fetch('/api/v1/staff/gifts/test-reserve?'+query,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      const body=await response.json() as {ok:boolean;data?:{
        reservations:Array<{
          reservationId:string;lineKey:string;productId:string;
          productTitle:string;priceCents:number;customerId:string|null;
          deliveryMode:'none'|'immediate'|'scheduled';
          recipientEmail:string|null;recipientName:string;senderName:string;
          personalMessage:string;scheduledAt:string|null;
        }>;};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.code??'Cannot load reserved Gift Cards.');
      return body.data.reservations;
    }).then(rows=>{
      if(controller.signal.aborted||!rows.length)return;
      onRestoreGiftLines(rows.map(gift=>({
        key:gift.lineKey,serviceId:'',giftCardId:gift.productId,
        name:gift.productTitle,priceCents:gift.priceCents,
        quantity:1,durationMinutes:0,appointmentId:null,
        reservedGift:{
          reservationId:gift.reservationId,customerId:gift.customerId,
          deliveryMode:gift.deliveryMode,recipientEmail:gift.recipientEmail,
          recipientName:gift.recipientName,senderName:gift.senderName,
          personalMessage:gift.personalMessage,scheduledAt:gift.scheduledAt,
        },
      })));
    }).catch(e=>{
      if(!controller.signal.aborted)
        setError(e instanceof Error?e.message:'Cannot load reserved Gift Cards.');
    });
    return ()=>controller.abort();
  },[draft.id,draft.ticketKey,draft.locationCode]);
  useEffect(()=>{
    // A missing buyer is not authority to erase a known reservation buyer.
    // Only synchronize an explicitly selected active customer.
    if(!draft.customerId)return;
    const rows=draft.lines.filter(line=>line.reservedGift&&
      line.reservedGift.customerId!==draft.customerId);
    if(!rows.length)return;
    const controller=new AbortController();
    void (async()=>{
      try{
        for(const line of rows){
          if(controller.signal.aborted)return;
          const result=await fetch('/api/v1/staff/gifts/test-reserve/'+
            encodeURIComponent(line.reservedGift!.reservationId)+'/buyer',{
            method:'PATCH',credentials:'same-origin',cache:'no-store',
            signal:controller.signal,
            headers:{'content-type':'application/json'},
            body:JSON.stringify({location:draft.locationCode,
              customerId:draft.customerId}),
          });
          const body=await result.json() as {ok:boolean;error?:{code:string}};
          if(!result.ok||!body.ok)
            throw Error(body.error?.code??'Gift Card buyer sync failed.');
        }
        // Update only the matching reservations in current app state.
        // Never overwrite a line added while the asynchronous buyer save ran.
        if(!controller.signal.aborted&&draft.customerId)
          onReconcileGiftBuyer(rows.map(l=>l.reservedGift!.reservationId),
            draft.customerId);
      }catch(e){
        if(!controller.signal.aborted)
          setError(e instanceof Error?e.message:'Gift Card buyer sync failed.');
      }
    })();
    return ()=>controller.abort();
  },[draft.customerId,draft.locationCode,draft.lines]);
  useEffect(()=>{
    const abort=new AbortController();
    setCatalogBusy(true);setCatalog(null);
    void fetch('/api/v1/staff/sales/omnibar?'+new URLSearchParams({
      location:draft.locationCode,
    }),{credentials:'same-origin',cache:'no-store',signal:abort.signal})
      .then(async response=>{
        const body=await response.json() as {ok:boolean;data?:OmniCatalog};
        if(!response.ok||!body.ok||!body.data)
          throw Error('Sale items could not be loaded.');
        return body.data;
      }).then(data=>{if(!abort.signal.aborted)setCatalog(data);})
      .catch(()=>{if(!abort.signal.aborted)setError('Sale items could not be loaded.');})
      .finally(()=>{if(!abort.signal.aborted)setCatalogBusy(false);});
    return ()=>abort.abort();
  },[draft.locationCode]);
  useEffect(()=>{
    if(!draft.customerId||draft.manualDiscount||draft.preselectedDiscount)return;
    const controller=new AbortController();
    void fetch('/api/v1/staff/sales/customer-auto-discount?'+
      new URLSearchParams({location:draft.locationCode,
        customerId:draft.customerId}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)return null;
      const result=await response.json() as {ok:boolean;data?:{discount:{
        id:string;code:string;title:string;description:string}|null}};
      return result.ok?result.data?.discount??null:null;
    }).then(discount=>{
      if(!controller.signal.aborted&&discount)
        onUpdateManualDiscount(discount);
    }).catch(()=>{/* No invented fallback discount when the source is absent. */});
    return ()=>controller.abort();
    // One lookup per selected sale/customer, not on every badge removal.
  },[draft.id,draft.customerId,draft.locationCode]);
  useEffect(()=>{
    if(!catalog||draft.autoItemInitialized)return;
    if(!draft.linkedAppointment){onAutoItemInitialized([]);return;}
    const appointments=draft.linkedAppointments?.length?
      draft.linkedAppointments:[draft.linkedAppointment];
    const rows:TicketLine[]=[];
    for(const linked of appointments){
      const match=catalog.services.find(item=>
        linked.serviceCode?item.code===linked.serviceCode:item.name===linked.serviceName);
      if(!match){setError('Appointment service is not available in this location’s catalog.');
        return;}
      rows.push({key:'appointment-'+linked.appointmentId,
        serviceId:match.id,name:match.name,durationMinutes:match.durationMinutes??0,
        priceCents:match.priceCents,quantity:1,appointmentId:linked.appointmentId});
    }
    onAutoItemInitialized(rows);
  },[catalog,draft.autoItemInitialized,draft.linkedAppointment,draft.linkedAppointments,onAutoItemInitialized]);
  useEffect(()=>{
    const controller=new AbortController();
    if(!draft.customerId){
      setCustomerReceiptPref(true);setReceiptEmail('');
      return ()=>controller.abort();
    }
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(draft.customerId)+
      '?'+new URLSearchParams({location:draft.locationCode}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('Receipt preference unavailable');
      const body=await response.json() as {ok:boolean;
        data?:{profile?:{email?:string|null;emailPreferences?:{saleReceipt?:boolean}};
          email?:string|null;emailPreferences?:{saleReceipt?:boolean}}};
      const profile=body.data?.profile??body.data;
      if(!controller.signal.aborted){
        setCustomerReceiptPref(profile?.emailPreferences?.saleReceipt!==false);
        setReceiptEmail(profile?.email??'');
      }
    }).catch(()=>{if(!controller.signal.aborted)setCustomerReceiptPref(true);});
    return ()=>controller.abort();
  },[draft.customerId,draft.locationCode]);
  // Debounced, cancellable search, matching the header's live lookup.
  useEffect(()=>{
    const term=query.trim();
    if(draft.customerId||term.length<2){
      setCandidates([]);setBusy(false);return;
    }
    const controller=new AbortController();
    setCandidates([]);setBusy(true);
    const timer=window.setTimeout(()=>{
      void fetch('/api/v1/staff/customers?'+new URLSearchParams({
        location:draft.locationCode,q:term,
      }),{credentials:'same-origin',cache:'no-store',signal:controller.signal})
        .then(async response=>{
          const body=await response.json() as CustomerBody;
          if(!response.ok||!body.ok||!body.data)
            throw Error(body.error?.message??'Customer search unavailable.');
          if(!controller.signal.aborted)setCandidates(body.data.customers);
        }).catch(e=>{
          if(!controller.signal.aborted)
            setError(e instanceof Error?e.message:'Customer search unavailable.');
        }).finally(()=>{if(!controller.signal.aborted)setBusy(false);});
    },250);
    return ()=>{window.clearTimeout(timer);controller.abort();};
  },[query,draft.customerId,draft.locationCode]);
  const cancel=async()=>{
    if(cancelling||giftBusy)return;
    setError('');setCancelling(true);
    try{
      for(const line of draft.lines.filter(x=>x.reservedGift)){
        const response=await fetch('/api/v1/staff/gifts/test-reserve/'+
          encodeURIComponent(line.reservedGift!.reservationId),{
          method:'DELETE',credentials:'same-origin',cache:'no-store',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({location:draft.locationCode}),
        });
        if(!response.ok)throw Error('Could not cancel reserved Gift Card.');
      }
      await onCancelDraft();
    }
    catch(e){setError(e instanceof Error?e.message:'Unable to cancel sale.');}
    finally{setCancelling(false);}
  };
  const results=useMemo(()=>{
    const typedCategory=parseOmnibarCategoryCommand(omniQuery);
    const activeCategory=browseCategory??typedCategory;
    const words=typedCategory?[]:
      omniQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return omniCategoryNames.filter(([key])=>!activeCategory||key===activeCategory)
      .map(([key,title])=>({
      key,title,items:(catalog?.[key]??[]).filter(item=>
        words.every(word=>(item.name+' '+(item.code??'')+' '+
          (item.durationMinutes??'')).toLowerCase().includes(word))),
    }));
  },[catalog,omniQuery,browseCategory]);
  const reserveGift=async(item:OmniItem)=>{
    if(giftBusy)return;
    setGiftBusy(true);setGiftStatus('');setError('');
    try{
      const key=crypto.randomUUID();
      const response=await fetch('/api/v1/staff/gifts/test-reserve',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:draft.locationCode,
          ticketKey:draft.ticketKey,lineKey:key,productId:item.id,
          customerId:draft.customerId}),
      });
      const result=await response.json() as {ok:boolean;data?:{
        reservationId:string;customerId:string|null;productTitle:string;priceCents:number;
        deliveryMode:'none'|'immediate'|'scheduled';
        recipientEmail:string|null;recipientName:string;senderName:string;
        personalMessage:string;scheduledAt:string|null;
      };error?:{code:string}};
      if(!response.ok||!result.ok||!result.data)
        throw Error(result.error?.code??'Unable to reserve Gift Card.');
      onUpdateLines([...draft.lines,{
        key,serviceId:'',giftCardId:item.id,
        reservedGift:{
          reservationId:result.data.reservationId,
          customerId:result.data.customerId,
          deliveryMode:result.data.deliveryMode,
          recipientEmail:result.data.recipientEmail,
          recipientName:result.data.recipientName,
          senderName:result.data.senderName,
          personalMessage:result.data.personalMessage,
          scheduledAt:result.data.scheduledAt,
        },name:result.data.productTitle,priceCents:result.data.priceCents,
        durationMinutes:0,quantity:1,appointmentId:null,
      }]);
      setOmniQuery('');setOmniActive(false);
      setGiftStatus('Gift Card number reserved. No payment or email has occurred.');
    }catch(e){setError(e instanceof Error?e.message:'Unable to reserve Gift Card.');}
    finally{setGiftBusy(false);}
  };
  const chooseGift=(line:TicketLine)=>{
    if(!line.reservedGift)return;
    setEditingGiftKey(line.key);setGiftStatus('');
    setGiftEmailMode(line.reservedGift.deliveryMode==='scheduled'?
      'scheduled':'immediate');
    setGiftRecipientEmail(line.reservedGift.recipientEmail??'');
    setGiftRecipientName(line.reservedGift.recipientName);
    setGiftSenderName(line.reservedGift.senderName);
    setGiftMessage(line.reservedGift.personalMessage);
    if(line.reservedGift.scheduledAt){
      const date=new Date(line.reservedGift.scheduledAt);
      const local=new Date(date.getTime()-date.getTimezoneOffset()*60000);
      setGiftSchedule(local.toISOString().slice(0,16));
    }else setGiftSchedule('');
  };
  const saveGiftEmail=async(line:TicketLine,clear=false)=>{
    if(!line.reservedGift||giftBusy)return;
    setGiftBusy(true);setError('');setGiftStatus('');
    try{
      const when=clear?undefined:giftEmailMode==='scheduled'?
        giftSchedule?new Date(giftSchedule).toISOString():undefined:undefined;
      if(!clear&&giftEmailMode==='scheduled'&&(!when||
        Date.parse(when)<=Date.now()))
        throw Error('Select a future date and time for scheduled delivery.');
      const response=await fetch('/api/v1/staff/gifts/test-reserve/'+
        encodeURIComponent(line.reservedGift.reservationId),{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:draft.locationCode,
          deliveryMode:clear?'none':giftEmailMode,
          ...(clear?{}:{recipientEmail:giftRecipientEmail,
            scheduledAt:when}),
          recipientName:clear?'':giftRecipientName,
          senderName:clear?'':giftSenderName,
          personalMessage:clear?'':giftMessage,
        }),
      });
      const result=await response.json() as {ok:boolean;data?:GiftDeliverySetup;
        error?:{code:string}};
      if(!response.ok||!result.ok||!result.data)
        throw Error(result.error?.code??'Unable to save Gift Card delivery.');
      onUpdateLines(draft.lines.map(item=>item.key===line.key?
        {...item,reservedGift:result.data}:item));
      setEditingGiftKey(null);
      setGiftStatus(clear?'Gift Card email setup cleared.':
        'Gift Card email configured, not sent. Delivery requires completed payment.');
    }catch(e){setError(e instanceof Error?e.message:'Unable to configure Gift Card.');}
    finally{setGiftBusy(false);}
  };
  const removeLine=async(line:TicketLine)=>{
    if(giftBusy)return;
    if(line.reservedGift){
      setGiftBusy(true);setError('');
      try{
        const response=await fetch('/api/v1/staff/gifts/test-reserve/'+
          encodeURIComponent(line.reservedGift.reservationId),{
          method:'DELETE',credentials:'same-origin',cache:'no-store',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({location:draft.locationCode}),
        });
        if(!response.ok)throw Error('Cannot cancel the reserved Gift Card.');
      }catch(e){setError(e instanceof Error?e.message:'Cannot remove Gift Card.');
        setGiftBusy(false);return;}
      setGiftBusy(false);
    }
    onUpdateLines(draft.lines.filter(item=>item.key!==line.key));
    if(editingGiftKey===line.key)setEditingGiftKey(null);
  };
  const add=(item:OmniItem)=>{
    if(!draft.customerId&&item.kind!=='giftCard'&&item.kind!=='product')return;
    if(item.kind==='discount'){
      onUpdateManualDiscount({id:item.id,code:item.code??'',title:item.name,
        description:item.description??''});
      setOmniQuery('');setOmniActive(false);return;
    }
    if(!['service','package','giftCard','membership','product'].includes(item.kind)||item.priceCents===null)return;
    if(item.kind==='giftCard'){void reserveGift(item);return;}
    const line:TicketLine={key:crypto.randomUUID(),
      serviceId:item.kind==='package'?item.serviceId??'':
        ['membership','product'].includes(item.kind)?'':item.id,
      ...(item.kind==='package'?{packageId:item.id,creditsPerUnit:item.creditsPerUnit,
        legacyCashPackageId:item.legacyCashPackageId}:{}),
      ...(item.kind==='product'?{productId:item.id}:{}),
      ...(item.kind==='membership'?{membershipId:item.id,
        recurringPriceCents:item.recurringPriceCents,repeatPeriod:item.repeatPeriod}:{}),
      name:item.name,
      durationMinutes:item.durationMinutes??0,priceCents:item.priceCents,
      quantity:1,appointmentId:null};
    onUpdateLines([...draft.lines,line]);setOmniQuery('');setOmniActive(false);
  };
  const selectedDiscount=draft.preselectedDiscount??draft.manualDiscount;
  const [verifiedQuote,setVerifiedQuote]=useState<{
    signature:string;discountId:string;code:string;
    lines:Array<{key:string;originalCents:number;discountCents:number}>;
    discountCents:number;
  }|null>(null);
  const hasSelectedDiscount=Boolean(selectedDiscount);
  const quotedLines=draft.lines.filter(l=>
    !l.giftCardId&&Boolean(l.productId||l.membershipId||l.packageId||l.serviceId));
  const quoteSignature=JSON.stringify({
    customerId:draft.customerId,location:draft.locationCode,
    discountId:selectedDiscount?.id??null,
    quotedLines:quotedLines.map(l=>({key:l.key,
      serviceId:l.packageId?null:l.serviceId,
      packageId:l.packageId??null,membershipId:l.membershipId??null,
      productId:l.productId??null,
      quantity:l.quantity,priceCents:l.priceCents})),
  });
  useEffect(()=>{
    setVerifiedQuote(null);
    if(!hasSelectedDiscount||!draft.customerId||!quotedLines.length)return;
    const controller=new AbortController();
    void fetch('/api/v1/staff/sales/auto-discount-quote',{
      method:'POST',credentials:'same-origin',cache:'no-store',
      headers:{'content-type':'application/json'},signal:controller.signal,
      body:JSON.stringify({location:draft.locationCode,
        customerId:draft.customerId,discountId:selectedDiscount!.id,
        lines:quotedLines.map(l=>({key:l.key,quantity:l.quantity,
          ...(l.productId?{productId:l.productId}:
            l.membershipId?{membershipId:l.membershipId}:
              l.packageId?{packageId:l.packageId}:{serviceId:l.serviceId})}))}),
    }).then(async response=>{
      const data=await response.json() as {ok:boolean;data?:{quote:{
        discountId:string;code:string;discountCents:number;
        lines:Array<{key:string;originalCents:number;discountCents:number}>;
      }}};
      if(!response.ok||!data.ok||!data.data?.quote)
        throw Error('Auto discount quote unavailable');
      return data.data.quote;
    }).then(quote=>{
      if(controller.signal.aborted||quote.discountId!==selectedDiscount!.id||
        quote.code.toLowerCase()!==selectedDiscount!.code.toLowerCase()||
        quote.lines.length!==quotedLines.length||
        quote.lines.some(row=>!quotedLines.some(l=>l.key===row.key&&
          row.originalCents===(l.priceCents??-1)*l.quantity&&
          row.discountCents>=0&&row.discountCents<=row.originalCents))||
        quote.lines.reduce((sum,row)=>sum+row.discountCents,0)!==quote.discountCents)
        return;
      setVerifiedQuote({...quote,signature:quoteSignature});
    }).catch(()=>{/* Never fabricate a financial discount on quote failure. */});
    return ()=>controller.abort();
  },[quoteSignature,hasSelectedDiscount]);
  const activeQuote=verifiedQuote?.signature===quoteSignature?verifiedQuote:null;
  const autoQuotePending=Boolean(hasSelectedDiscount&&draft.lines.length&&
    (draft.lines.some(l=>Boolean(l.giftCardId))||
      quotedLines.length!==draft.lines.length||!activeQuote));
  const discountFor=(key:string)=>activeQuote?.lines.find(l=>l.key===key)?.discountCents??0;

  const selectedCode=catalog?.discounts.find(item=>item.id===selectedDiscount?.id);
  // Show only configured Setup constraints; historical customer eligibility
  // cannot be reconstructed from a code's total imported use count.
  const discountConditions:string[]=[];
  if(selectedCode?.newOnly)discountConditions.push('New Only: Yes');
  if(selectedCode?.mailingListOnly)discountConditions.push('Mailing List Only: Yes');
  if(selectedCode?.useLimit!==undefined&&selectedCode.useLimit!==null)
    discountConditions.push('Overall use limit: '+selectedCode.useLimit+
      ' (imported uses: '+(selectedCode.uses??'unknown')+')');
  if(selectedCode?.perCustomerLimit!==undefined&&selectedCode.perCustomerLimit!==null)
    discountConditions.push('Per customer limit: '+selectedCode.perCustomerLimit);
  if(selectedCode?.availableFrom)
    discountConditions.push('Available from: '+selectedCode.availableFrom);
  if(selectedCode?.availableUntil)
    discountConditions.push('Available until: '+selectedCode.availableUntil);
  if(selectedCode?.minPurchaseCents!==undefined&&selectedCode.minPurchaseCents!==null)
    discountConditions.push('Minimum purchase: '+dollar(selectedCode.minPurchaseCents));
  if(selectedCode?.employeeInstructions?.trim())
    discountConditions.push('Staff instructions: '+selectedCode.employeeInstructions.trim());
  const sourceDiscountRule=draft.manualDiscount&&draft.manualDiscount.id===selectedDiscount?.id
    ?draft.manualDiscount.description:selectedCode?.description??'';
  const discountDetails=['Code: '+(selectedDiscount?.code??''),
    'Source discount rule: '+sourceDiscountRule,
    ...discountConditions].join('\n');
  const hasGiftCards=draft.lines.some(line=>Boolean(line.giftCardId));
  const hasMemberships=draft.lines.some(line=>Boolean(line.membershipId));
  const cashNeedsCustomer=draft.lines.some(line=>!line.productId);
  const unsupportedCashPackage=draft.lines.some(line=>Boolean(line.packageId)&&
    !line.legacyCashPackageId);
  const subtotal=draft.lines.reduce((v,line)=>
    v+(line.priceCents??0)*line.quantity,0);
  const allPriced=draft.lines.every(line=>line.priceCents!==null);
  const totalCents=Math.max(0,subtotal-(activeQuote?.discountCents??0));
  const priceReady=allPriced&&!autoQuotePending;
  // A paid TEST checkout can close only the exact items and total that were
  // submitted. Later ticket edits must not inherit a previous paid status.
  const saleSnapshot=JSON.stringify({location:draft.locationCode,
    customerId:draft.customerId,discountId:selectedDiscount?.id??null,
    lines:draft.lines.map(line=>({key:line.key,serviceId:line.serviceId,
      packageId:line.packageId,productId:line.productId,
      giftCardId:line.giftCardId,membershipId:line.membershipId,
      appointmentId:line.appointmentId,quantity:line.quantity,
      priceCents:line.priceCents})),totalCents});
  const cardSaleEligible=Boolean(draft.customerId)&&draft.lines.length>0&&
    priceReady&&totalCents>0&&!selectedDiscount&&
    draft.lines.every(l=>Boolean(l.serviceId||l.packageId)&&!l.productId&&
      !l.giftCardId&&!l.membershipId&&l.quantity>=1&&
      (!l.appointmentId||l.quantity===1))&&
    (!draft.linkedAppointment||draft.lines.some(l=>l.appointmentId))&&
    (!draft.linkedAppointments?.length||draft.lines.some(l=>l.appointmentId));
  const retailCardEligible=draft.lines.length>0&&priceReady&&totalCents>=50&&
    !selectedDiscount&&draft.lines.every(l=>Boolean(l.productId)&&
      !l.serviceId&&!l.packageId&&!l.giftCardId&&!l.membershipId&&
      !l.appointmentId&&l.quantity>=1)&&
    !draft.linkedAppointment&&!(draft.linkedAppointments?.length);
  const discountedCardEligible=Boolean(draft.customerId&&selectedDiscount&&
    activeQuote&&priceReady&&totalCents>0&&subtotal>totalCents)&&
    draft.lines.length===1&&draft.lines[0].quantity===1&&
    Boolean(draft.lines[0].serviceId)&&!draft.lines[0].packageId&&
    !draft.lines[0].productId&&!draft.lines[0].giftCardId&&
    !draft.lines[0].membershipId&&!draft.lines[0].appointmentId&&
    !draft.linkedAppointment&&!(draft.linkedAppointments?.length)&&
    activeQuote?.lines.length===1&&
    activeQuote.lines[0].originalCents===subtotal&&
    activeQuote.lines[0].discountCents===subtotal-totalCents;
  const complimentaryEligible=Boolean(draft.customerId&&selectedDiscount)&&
    priceReady&&subtotal>0&&totalCents===0&&
    activeQuote?.discountCents===subtotal&&
    activeQuote?.lines.length===1&&draft.lines.length===1&&
    draft.lines[0].quantity===1&&Boolean(draft.lines[0].serviceId)&&
    !draft.lines[0].packageId&&!draft.lines[0].productId&&
    !draft.lines[0].giftCardId&&!draft.lines[0].membershipId&&
    (!draft.linkedAppointment||
      draft.lines[0].appointmentId===draft.linkedAppointment.appointmentId)&&
    (!draft.linkedAppointments?.length||
      (draft.linkedAppointments.length===1&&
        draft.lines[0].appointmentId===draft.linkedAppointments[0].appointmentId));
  // A red ! means a condition is actually unmet, not merely that a code
  // has limits. Person-level history is not available to evaluate here.
  const unmetDiscountConditions:string[]=[];
  if(selectedCode?.useLimit!==undefined&&selectedCode.useLimit!==null&&
    selectedCode.uses!==undefined&&selectedCode.uses>=selectedCode.useLimit)
    unmetDiscountConditions.push('Overall use limit reached: '+
      selectedCode.uses+' of '+selectedCode.useLimit);
  if(selectedCode?.minPurchaseCents!==undefined&&
    selectedCode.minPurchaseCents!==null&&allPriced&&
    subtotal<selectedCode.minPurchaseCents)
    unmetDiscountConditions.push('Minimum purchase: '+
      dollar(selectedCode.minPurchaseCents)+'; current subtotal: '+dollar(subtotal));
  useEffect(()=>{
    
    setReceiptChecked(totalCents>0&&customerReceiptPref);
  },[totalCents,customerReceiptPref]);
  const tenderTotal=/^\d+(?:\.\d{0,2})?$/.test(tenderAmount)?
    Math.round(Number(tenderAmount)*100):0;
  const changeDue=Math.max(0,tenderTotal-totalCents);
  const ticketName=draft.customerName??'walk-in';
  const showCash=async()=>{
    // A zero-dollar checkout must never route through a cash tender.
    if(!draft.lines.length||!priceReady||totalCents<=0||cardBusy||cardOrderId||
       selectedDiscount||hasGiftCards||hasMemberships||unsupportedCashPackage||
       (cashNeedsCustomer&&!draft.customerId))return;
    setCashOpen(true);setError('');
    try{
      const r=await fetch('/api/v1/staff/sales/cash-register?'+
        new URLSearchParams({location:draft.locationCode}),
        {credentials:'same-origin',cache:'no-store'});
      const result=await r.json() as {ok:boolean;data?:{register:typeof register}};
      if(!r.ok||!result.ok||!result.data?.register)
        throw Error('Cash register is not configured for this location.');
      setRegister(result.data.register);
    }catch(e){setError(e instanceof Error?e.message:'Cash register unavailable.');}
  };
  const completeCash=async()=>{
    if((cashNeedsCustomer&&!draft.customerId)||!draft.lines.length||!allPriced||
       !priceReady||totalCents<=0||tenderTotal<totalCents||cashBusy||
       cardBusy||cardOrderId||selectedDiscount||
       hasGiftCards||hasMemberships||unsupportedCashPackage)return;
    setCashBusy(true);setError('');
    try{
      const r=await fetch('/api/v1/staff/sales/cash',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:draft.locationCode,transactionId,customerId:draft.customerId,
          tenderCents:tenderTotal,
          lines:draft.lines.map(item=>({
            ...(item.productId?{productId:item.productId}:
              item.packageId?{packageId:item.legacyCashPackageId}:{serviceId:item.serviceId}),
            quantity:item.quantity,appointmentId:item.appointmentId})),
          openTicketAppointmentId:draft.linkedAppointment?.appointmentId??null,
          linkedAppointmentIds:(draft.linkedAppointments?.length?
            draft.linkedAppointments:[draft.linkedAppointment].filter(Boolean))
            .map(a=>a!.appointmentId),
        }),
      });
      const data=await r.json() as {ok:boolean;data?:{receipt:CashReceipt};
        error?:{message?:string;code?:string}};
      if(!r.ok||!data.ok||!data.data)
        throw Error(data.error?.message??data.error?.code??'Unable to complete cash checkout.');
      setReceipt(data.data.receipt);
    }catch(e){setError(e instanceof Error?e.message:'Unable to complete cash checkout.');}
    finally{setCashBusy(false);}
  };
  useEffect(()=>{
    if(!cardOrderId||(!cardOrderIsRetail&&!draft.customerId)||
      cardOrderStatus==='FULFILLED'||cardOrderStatus==='RETAIL_PAID')return;
    let active=true;
    const poll=async()=>{
      try{
        const endpoint=cardOrderIsRetail?
          '/api/v1/staff/sales/stripe/test-retail-orders/':
          '/api/v1/staff/sales/stripe/test-orders/';
        const query=new URLSearchParams({location:draft.locationCode});
        if(!cardOrderIsRetail)query.set('customerId',draft.customerId!);
        const response=await fetch(endpoint+
          encodeURIComponent(cardOrderId)+'?'+query,
          {credentials:'same-origin',cache:'no-store'});
        const body=await response.json() as {ok:boolean;data?:{
          status:string;paid:boolean;fulfilled:boolean}};
        if(active&&response.ok&&body.ok&&body.data){
          if(cardOrderIsRetail&&body.data.paid)
            setCardOrderStatus('RETAIL_PAID');
          else if(body.data.fulfilled)setCardOrderStatus('FULFILLED');
          else if(body.data.paid)setCardOrderStatus('PAID_UNFULFILLED');
        }
      }catch{/* Keep the order pending until signed webhook status is available. */}
    };
    void poll();
    const interval=window.setInterval(()=>void poll(),3000);
    return ()=>{active=false;window.clearInterval(interval);};
  },[cardOrderId,cardOrderIsRetail,cardOrderStatus,draft.customerId,draft.locationCode]);
  const completeComplimentary=async()=>{
    if(!complimentaryEligible||!draft.customerId||!selectedDiscount||
      complimentaryBusy)return;
    setComplimentaryBusy(true);setError('');
    try{
      const line=draft.lines[0];
      const response=await fetch('/api/v1/staff/sales/discount-complimentary',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:draft.locationCode,transactionId,
          customerId:draft.customerId,serviceId:line.serviceId,
          discountId:selectedDiscount.id,appointmentId:line.appointmentId}),
      });
      const result=await response.json() as {ok:boolean;data?:{
        receipt:ComplimentaryReceipt};error?:{code:string}};
      if(!response.ok||!result.ok||!result.data?.receipt||
        result.data.receipt.netCents!==0||
        result.data.receipt.discountCents!==result.data.receipt.originalCents)
        throw Error(result.error?.code??'Complimentary sale unavailable.');
      setComplimentaryReceipt(result.data.receipt);
    }catch(e){setError(e instanceof Error?e.message:
      'Complimentary sale unavailable.');}
    finally{setComplimentaryBusy(false);}
  };
  const blankTicket=draft.lines.length===0&&!draft.linkedAppointment&&
    !(draft.linkedAppointments?.length)&&!cardOrderId;
  const verifiedCardSale=(cardOrderStatus==='FULFILLED'||
    cardOrderStatus==='RETAIL_PAID')&&
    cardCheckoutSnapshot===saleSnapshot;
  const closeSaleAllowed=!cashBusy&&!cardBusy&&!complimentaryBusy&&!commentSaving&&
    !cancelling&&priceReady&&
    (blankTicket||verifiedCardSale||complimentaryEligible);
  const finishRecordedSale=async(source:'CASH'|'STRIPE_TEST'|'STRIPE_TEST_RETAIL'|'COMPLIMENTARY',
    saleId:string)=>{
    if(commentSaving)return;
    const note=(draft.saleComment??'').trim();
    if(note){
      setCommentSaving(true);setCommentError('');
      try{
        const response=await fetch('/api/v1/staff/sales/comments',{
          method:'POST',credentials:'same-origin',cache:'no-store',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({location:draft.locationCode,source,saleId,
            idempotencyKey:draft.ticketKey,comment:note}),
        });
        const data=await response.json() as {ok:boolean;error?:{code?:string}};
        if(!response.ok||!data.ok)throw Error(data.error?.code??'Purchase comment unavailable.');
      }catch(e){
        setCommentError(e instanceof Error?e.message:'Purchase comment could not be saved.');
        setCommentSaving(false);return;
      }
      setCommentSaving(false);
    }
    onComplete();
  };
  const closeSale=()=>{
    if(!closeSaleAllowed)return;
    if(blankTicket){onComplete();return;}
    if(verifiedCardSale&&cardOrderId){
      void finishRecordedSale(cardOrderIsRetail?'STRIPE_TEST_RETAIL':
        'STRIPE_TEST',cardOrderId);return;
    }
    if(complimentaryEligible)void completeComplimentary();
  };
  const rename=(next:Customer|null)=>{
    if(next===null&&draft.lines.some(line=>!line.giftCardId&&!line.productId)){
      setError('Remove customer-specific items before creating a walk-in ticket.');
      return;
    }
    onUpdateCustomer(next);setCandidates([]);setQuery('');setError('');
  };
  return <section className="ecfs-sales-ticket" role="dialog"
    aria-label={'Sale ticket for '+ticketName}>
    <header className="ecfs-sale-header">
      <small>{draft.linkedAppointment
        ?'Appointment sale · '+draft.linkedAppointment.saleId.slice(0,8)
        :'Sale · '+draft.id} at East Coast Float Spa {locationName}</small>
      <button type="button" className="ecfs-sale-comment-toggle"
        aria-label="Sale comment" title="Sale comment"
        aria-expanded={commentOpen} onClick={()=>setCommentOpen(v=>!v)}>
        <span aria-hidden="true">▤</span></button>
      <button type="button" className="ecfs-sale-help-icon"
        aria-label="Point of Sale help" title="Point of Sale"
        onClick={()=>setHelpMode('pos')}>?</button>
      <button type="button" aria-label="Back to calendar" title="Back to calendar"
        onClick={()=>receipt?void finishRecordedSale('CASH',receipt.transactionId):
          complimentaryReceipt?void finishRecordedSale('COMPLIMENTARY',complimentaryReceipt.transactionId):
          onMinimize()}>×</button>
    </header>
    {commentOpen&&<div className="ecfs-sale-comment-popover" role="dialog"
      aria-label="Sale comment editor">
      <label>Sale comment
        <textarea aria-label="Sale comment text" rows={3} maxLength={5000}
          disabled={commentSaving}
          placeholder="Add a comment to this purchase"
          value={draft.saleComment??''}
          onChange={e=>onUpdateSaleComment(e.target.value)}/>
      </label>
      <button type="button" onClick={()=>setCommentOpen(false)}>Done</button>
    </div>}
    {commentError&&<p role="alert">{commentError}</p>}
    {receipt?<div className="ecfs-sale-receipt" role="status">
      <h3>Cash sale complete</h3>
      <p>Total: <strong>{dollar(receipt.totalCents)}</strong></p>
      <p>Cash received: <strong>{dollar(receipt.receivedCents)}</strong></p>
      <p>Change due: <strong>{dollar(receipt.changeCents)}</strong></p>
      {Object.values(receipt.changeDenominations).some(count=>count>0)&&
        <p>Change denominations: {Object.entries(receipt.changeDenominations)
          .filter(([,count])=>count>0).map(([denom,count])=>
            count+' × '+billName(Number(denom))).join(', ')}</p>}
      <button type="button" disabled={commentSaving}
        onClick={()=>void finishRecordedSale('CASH',receipt.transactionId)}>
        {commentSaving?'Saving sale comment…':'Done'}</button>
    </div>:complimentaryReceipt?<div className="ecfs-sale-receipt" role="status">
      <h3>Sale complete</h3>
      <p>Source discount: <strong>-{dollar(complimentaryReceipt.discountCents)}</strong></p>
      <p>Total: <strong>{dollar(0)}</strong></p>
      <p>No cash or card was charged.</p>
      <button type="button" disabled={commentSaving}
        onClick={()=>void finishRecordedSale('COMPLIMENTARY',complimentaryReceipt.transactionId)}>
        {commentSaving?'Saving sale comment…':'Done'}</button>
    </div>:<>
      <div className="ecfs-sales-ticket-body">
        <div className="ecfs-sale-column-titles">
          <div aria-hidden="true"/>
          <strong>Qty</strong><strong>Price</strong><strong>Discount</strong><strong>Total</strong>
        </div>
        <div className="ecfs-sale-customer-line">
          <div className="ecfs-sale-customer-name">
            {draft.customerName
              ?<><strong>{draft.customerName}</strong>
                <button type="button" onClick={()=>rename(null)}>change</button></>
              :<div className="ecfs-sale-customer-lookup">
                <input type="search" autoComplete="off" aria-label="Find sale customer"
                  placeholder="Search customer" value={query}
                  onChange={e=>setQuery(e.target.value)}/>
                <button type="button" onClick={onNewCustomer}>New Customer</button>
                {busy&&<small role="status">Searching…</small>}
              </div>}
            {candidates.length>0&&<div className="ecfs-sale-customer-results"
              aria-label="Matching sale customers">
              {candidates.map(c=><button type="button" key={c.customerId}
                onClick={()=>rename(c)}>{c.displayName}</button>)}
            </div>}
          </div>
          <div className="ecfs-sale-receipt-tools">
            <label className="ecfs-sale-receipt-option">
              <input type="checkbox" checked={receiptChecked}
                onChange={event=>setReceiptChecked(event.target.checked)}/> Email Receipt
            </label>
            {(!draft.customerId||!receiptEmail)&&<button type="button"
              className="ecfs-sale-add-email" onClick={()=>setReceiptEmailEdit(v=>!v)}>
              (add email)</button>}
            {receiptEmailEdit&&<input type="email" aria-label="Sale receipt email"
              placeholder="Email address" value={receiptEmail}
              onChange={event=>setReceiptEmail(event.target.value)}/>}
          </div>
        </div>
        <div className="ecfs-sale-items" aria-label="Sale items">
          {draft.lines.length?draft.lines.map(line=>
            <div className="ecfs-sale-item-row" key={line.key}>
              <span>{line.name}
                <small>{line.giftCardId?'Gift Card · Unpaid · Not redeemable':
                  line.membershipId?'Membership · First payment '+dollar(line.priceCents??0)+
                    ' · Recurring '+(line.recurringPriceCents==null?
                      'not recorded':dollar(line.recurringPriceCents))+
                    ' '+(line.repeatPeriod??'')+' · Not enrolled':
                  line.packageId?(line.creditsPerUnit?line.creditsPerUnit+' service credits':'Package')+' · Unpaid':
                  line.productId?'Product · Unpaid':
                  line.appointmentId?'Appointment · Unpaid':
                  line.durationMinutes+'-minute service · Unpaid'}</small>
                {line.reservedGift&&<small>Reserved Gift Card #:
                  {' '}{line.reservedGift.reservationId}</small>}
                {line.reservedGift&&<button type="button"
                  disabled={giftBusy} onClick={()=>chooseGift(line)}>
                  Email Gift Card</button>}
                {line.reservedGift&&line.reservedGift.deliveryMode!=='none'&&
                  <small>Delivery setup: {line.reservedGift.deliveryMode==='immediate'?
                    'after completed sale':'scheduled, only after completed sale'}
                  </small>}
                <button type="button" className="ecfs-remove-sale-item"
                  disabled={giftBusy} aria-label={'Remove '+line.name}
                  onClick={()=>void removeLine(line)}>×</button>
                {line.reservedGift&&editingGiftKey===line.key&&
                  <form className="ecfs-gift-email-setup"
                    aria-label={'Email Gift Card '+line.reservedGift.reservationId}
                    onSubmit={e=>{e.preventDefault();void saveGiftEmail(line);}}>
                    <strong>Email Gift Card (setup only)</strong>
                    <label>Recipient Email (required to email)
                      <input type="email" required value={giftRecipientEmail}
                        onChange={e=>setGiftRecipientEmail(e.target.value)}/></label>
                    <label>Recipient Name (optional)
                      <input maxLength={120} value={giftRecipientName}
                        onChange={e=>setGiftRecipientName(e.target.value)}/></label>
                    <label>Sender Name (optional)
                      <input maxLength={120} value={giftSenderName}
                        onChange={e=>setGiftSenderName(e.target.value)}/></label>
                    <label>Personal Message (optional)
                      <textarea maxLength={600} value={giftMessage}
                        onChange={e=>setGiftMessage(e.target.value)}/></label>
                    <label>Delivery Time
                      <select value={giftEmailMode} onChange={e=>
                        setGiftEmailMode(e.target.value as 'immediate'|'scheduled')}>
                        <option value="immediate">When this sale is completed</option>
                        <option value="scheduled">Schedule for later</option>
                      </select></label>
                    {giftEmailMode==='scheduled'&&<label>Scheduled Date and Time
                      <input type="datetime-local" required value={giftSchedule}
                        onChange={e=>setGiftSchedule(e.target.value)}/></label>}
                    <button type="submit" disabled={giftBusy}>
                      {giftBusy?'Saving…':'Save Email Gift Card Setup'}</button>
                    <button type="button" disabled={giftBusy}
                      onClick={()=>void saveGiftEmail(line,true)}>
                      Clear Email Setup</button>
                    <button type="button" onClick={()=>setEditingGiftKey(null)}>
                      Close</button>
                    <p>Saving does not send a gift. A verified paid sale is
                      required before any Gift Card is issued or delivered.</p>
                  </form>}
              </span>
              <span>{line.quantity}</span>
              <span>{line.priceCents===null?'—':dollar(line.priceCents)}</span>
              <span>{discountFor(line.key)>0?'-'+dollar(discountFor(line.key)):'—'}</span>
              <span>{line.priceCents===null||!priceReady?'—':
                dollar(line.priceCents*line.quantity-discountFor(line.key))}</span>
            </div>):<p className="ecfs-sale-no-items">No items yet</p>}
        </div>
        <div className="ecfs-sale-tools">
          <div className="ecfs-omnibar-anchor">
            <input type="search" aria-label="Search sale items" autoComplete="off"
              placeholder="Search services, packages, memberships…"
              value={omniQuery} onFocus={()=>setOmniActive(true)}
              onClick={()=>setOmniActive(true)}
              onChange={event=>{setBrowseCategory(null);
                setOmniQuery(event.target.value);setOmniActive(true);}}/>
            <button type="button" className="ecfs-sale-help-icon ecfs-omnibar-help-icon"
              aria-label="Omnibar commands help" title="Sale Omnibar Commands"
              onClick={()=>setHelpMode('omnibar')}>?</button>
            {omniActive&&<div className="ecfs-omnibar-results"
              role="region" aria-label="Sale item search results">
              {catalogBusy&&<p role="status">Loading items…</p>}
              {catalog&&!catalogBusy&&results.map(group=><div key={group.key} className="ecfs-omnibar-group">
                <strong>{group.title}</strong>
                {!draft.customerId&&!['giftCards','products'].includes(group.key)&&
                  <small>Select a customer to add these items.</small>}
                {group.items.length?group.items.map(item=>
                  <button type="button" key={item.kind+item.id}
                    disabled={giftBusy||(!draft.customerId&&!['giftCard','product'].includes(item.kind))||
                      !['service','package','giftCard','membership','discount','product'].includes(item.kind)||
                      (item.kind!=='discount'&&item.priceCents===null)}
                    onClick={()=>add(item)}>
                    <span>{item.name}{item.durationMinutes?' · '+
                      item.durationMinutes+' min':''}</span>
                    <small>{item.kind==='discount'?(item.description??'Discount Code'):
                      item.priceCents!==null?dollar(item.priceCents)+
                        (item.kind==='membership'?' first payment':''):'Not yet purchasable'}</small>
                  </button>):<small>No matching {group.title.toLowerCase()}</small>}
              </div>)}
              <button type="button" className="ecfs-omnibar-close"
                onClick={()=>setOmniActive(false)}>Close</button>
            </div>}
          </div>
          <div className="ecfs-sale-totals">
            <span>Subtotal: {allPriced?dollar(subtotal):'—'}</span>
            <span>Tax: —</span>
            <strong>Total: {priceReady?dollar(totalCents):'—'}</strong>
          </div>
        </div>
        {selectedDiscount&&<section className="ecfs-sale-discount-badge"
          aria-label="Selected sale discount">
          <div className="ecfs-sale-discount-label">
            <strong>{selectedDiscount.title}</strong>
            <button type="button" aria-label="Discount details"
              title={discountDetails}>
              ⓘ
            </button>
            {unmetDiscountConditions.length>0&&
              <button type="button" className="ecfs-sale-discount-warning"
                aria-label="Unmet discount conditions"
                title={unmetDiscountConditions.join('\n')}>
                !
              </button>}
            <button type="button" aria-label="Remove selected discount"
              disabled={removingDiscount} onClick={async()=>{
                if(draft.preselectedDiscount){
                  setRemovingDiscount(true);setError('');
                  try{await onRemovePreselectedDiscount();}
                  catch(e){setError(e instanceof Error?e.message:'Could not remove discount.');}
                  finally{setRemovingDiscount(false);}
                }else onUpdateManualDiscount(null);
              }}>×</button>
          </div>
        </section>}
        <div className="ecfs-sale-payment-row">
          <div className="ecfs-sale-payment-methods" aria-label="Payment methods">
            {hasMemberships?<StaffMembershipPayment
              location={draft.locationCode} customerId={draft.customerId}
              productId={draft.lines.length===1?
                draft.lines[0].membershipId??null:null}
              ticketKey={draft.ticketKey} onComplete={onComplete}
              eligible={Boolean(draft.customerId)&&draft.lines.length===1&&
                Boolean(draft.lines[0].membershipId)&&
                draft.lines[0].quantity===1&&
                draft.lines[0].priceCents!==null&&
                draft.lines[0].recurringPriceCents!==null&&
                priceReady&&!selectedDiscount&&!draft.linkedAppointment&&
                !(draft.linkedAppointments?.length)}/>
            :hasGiftCards?<StaffGiftCardPayment
              location={draft.locationCode} customerId={draft.customerId}
              ticketKey={draft.ticketKey}
              reservationIds={draft.lines.flatMap(l=>
                l.reservedGift?[l.reservedGift.reservationId]:[])}
              onComplete={onComplete}
              eligible={Boolean(draft.customerId)&&draft.lines.length>0&&
                priceReady&&!selectedDiscount&&
                !draft.linkedAppointment&&!(draft.linkedAppointments?.length)&&
                draft.lines.every(l=>Boolean(l.giftCardId)&&
                  Boolean(l.reservedGift)&&
                  l.reservedGift?.customerId===draft.customerId&&
                  l.quantity===1)}/>
            :complimentaryEligible?<button type="button"
              disabled={complimentaryBusy} onClick={()=>void completeComplimentary()}>
              {complimentaryBusy?'Completing Sale…':'Complete Sale'}</button>
            :<StaffTicketCardPayment
              location={draft.locationCode} customerId={draft.customerId}
              ticketKey={draft.ticketKey} totalCents={totalCents}
              saleSnapshot={saleSnapshot} lines={draft.lines}
              retailLines={retailCardEligible?draft.lines.map(l=>({
                productId:l.productId!,quantity:l.quantity})):undefined}
              eligible={cardSaleEligible||discountedCardEligible||retailCardEligible}
              discount={discountedCardEligible?{
                serviceId:draft.lines[0].serviceId,
                discountId:selectedDiscount!.id}:null}
              settled={cardOrderStatus==='FULFILLED'||cardOrderStatus==='RETAIL_PAID'}
              onBusyChange={setCardBusy}
              onStarted={(orderId,snapshot)=>{
                setCashOpen(false);
                setCardOrderId(orderId);setCardOrderIsRetail(retailCardEligible);
                setCardOrderStatus('CHECKOUT_OPEN');
                setCardCheckoutSnapshot(snapshot);
              }}/>}
            <button type="button" onClick={()=>void showCash()}
              disabled={(cashNeedsCustomer&&!draft.customerId)||!draft.lines.length||!priceReady||
                totalCents<=0||cardBusy||Boolean(cardOrderId)||Boolean(selectedDiscount)||
                hasGiftCards||hasMemberships||unsupportedCashPackage}>Cash</button>
            <button disabled type="button">Gift Card</button>
            <button disabled type="button">CashApp</button>
          </div>

        </div>
        {cardOrderId&&<section className="ecfs-cash-tender" aria-label="Card payment status">
          <h3>Stripe TEST Card</h3>
          <p role="status">{cardOrderStatus==='RETAIL_PAID'
            ?'Retail payment verified by Stripe. Sale recorded without issuing service credits.'
            :cardOrderStatus==='FULFILLED'
            ?'Payment verified by Stripe. Service credits issued and selected appointment payments completed.'
            :cardOrderStatus==='PAID_UNFULFILLED'
              ?'Stripe payment recorded. Service credit fulfillment is processing.'
              :'Card payment pending. Payment is not yet verified; no credits have been issued.'}</p>
          <small>Order: {cardOrderId}</small>

        </section>}
        {cashOpen&&!cardOrderId&&priceReady&&totalCents>0&&
          <section className="ecfs-cash-tender" aria-label="Cash tender">
          <h3>Cash</h3>
          <label>Cash received
            <input type="number" min="0" step="0.01" inputMode="decimal"
              aria-label="Cash received" value={tenderAmount}
              onChange={e=>setTenderAmount(e.target.value)}/>
          </label>
          <p>Amount due: <strong>{priceReady?dollar(totalCents):'—'}</strong>. Tendered:
            <strong> {dollar(tenderTotal)}</strong>. Change due:
            <strong> {dollar(changeDue)}</strong></p>
          {!draft.customerId&&cashNeedsCustomer&&<p>Select a customer to complete a sale.</p>}

          {hasGiftCards&&<p role="alert">Gift-card sale fulfillment is not enabled yet.</p>}
          <button type="button" onClick={()=>void completeCash()}
            disabled={cashBusy||!register||(cashNeedsCustomer&&!draft.customerId)||
              draft.lines.length===0||!priceReady||totalCents<=0||tenderTotal<totalCents||
              Boolean(cardOrderId)||cardBusy||Boolean(selectedDiscount)||
              hasGiftCards||hasMemberships||unsupportedCashPackage}>
            {cashBusy?'Completing cash sale…':'Complete Cash Sale'}
          </button>
          <button type="button" onClick={()=>setCashOpen(false)}>Back</button>
        </section>}
      </div>
      <footer className="ecfs-sale-ticket-footer">
        {giftStatus&&<p role="status">{giftStatus}</p>}
        {error&&<p role="alert">{error}</p>}
        {cancelConfirm?<div className="ecfs-cancel-sale-confirm" role="group"
          aria-label="Confirm cancel sale">
          <button type="button" className="ecfs-cancel-sale" disabled={cancelling}
            onClick={()=>void cancel()}>
            {cancelling?'Cancelling…':'Cancel Sale'}</button>
          <button type="button" disabled={cancelling}
            className="ecfs-keep-sale" onClick={()=>setCancelConfirm(false)}>
            or keep it</button>
        </div>:<button type="button" className="ecfs-cancel-sale"
          disabled={cancelling} onClick={()=>setCancelConfirm(true)}>
          Cancel Sale</button>}
        <button type="button" className="ecfs-close-sale" onClick={closeSale}
          disabled={!closeSaleAllowed} title={!closeSaleAllowed?
            'Sale must be eligible and payment verified before closing.':undefined}>
          Close Sale {priceReady?dollar(totalCents):'—'}
        </button>
      </footer>
    </>}
    {helpMode&&<SaleTicketHelp mode={helpMode}
      counts={omniCategoryNames.map(([key])=>({key,count:catalog?.[key]?.length??0}))}
      onClose={()=>setHelpMode(null)} onHelpFeedback={onHelpFeedback}
      onBrowse={kind=>{setBrowseCategory(kind);setOmniQuery('');
        setOmniActive(true);}}/>}
  </section>;
}
